import * as React from "react";
import { useSearchParams } from "react-router";
import { DriveHeader } from "../driveHeader";
import { DriveList } from "../driveList";
import { Pagination } from "../pagination";
import { DriveActionBar } from "./DriveActionBar";
import { ContractSummaryStatusBanner } from "./ContractSummaryStatusBanner";
import { Item, FolderItem, FileItem } from "@/types/fse";
import { SortOption } from "@/components/sortDropdown";
import { DriveToolbar } from "@/views/drive/workspace/DriveToolbar";
import { DriveSkeleton } from "@/views/drive/driveSkeleton";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useContractFamiliesAccess } from "@/hooks/use-contract-families-access";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { collectDirectoryUserIds } from "@/views/drive/workspace/utils";
import { DriveEmptyState, DriveFatalError } from "@/views/drive/workspace/DriveWorkspaceStates";
import { DriveWorkspaceHeader } from "@/views/drive/workspace/DriveWorkspaceHeader";
import { useSupplierContractFolder } from "@/views/drive/useSupplierContractFolder";
import { isContractFamilyLink } from "@/views/drive/supplierContractFamilyNavigation";
import { Skeleton } from "@/components/ui/skeleton";
import { DocumentMapViewControl } from "@/views/drive/documentMap/DocumentMapViewControl";
import {
  buildDocumentGraph,
  documentRoleLabel,
  graphStructureSummary,
  mapBannerSummary,
} from "@/views/drive/documentMap/graphModel";
import { useDocumentMapViewMode } from "@/views/drive/documentMap/useDocumentMapViewMode";
import { useFamilyFolderDocumentMap } from "@/views/drive/documentMap/useFamilyFolderDocumentMap";

// The graph libraries load only where a map is shown, not on the drive's first paint.
const DocumentMapPanel = React.lazy(() =>
  import("@/views/drive/documentMap/DocumentMapPanel").then(module => ({
    default: module.DocumentMapPanel,
  })),
);

interface Props {
  // Header props
  currentFolder: FolderItem | undefined | null;
  parentFolder: FolderItem | undefined | null;

  // Search props
  folderId?: string;

  // Action bar props
  selectedItemsCount: number;
  selectedFileItems: FileItem[];
  onDeleteClick: () => void;
  isDeleteLoading: boolean;
  currentSort: SortOption;
  onSortChange: (sort: SortOption) => void;

  // List props
  items: Item[];
  isRootFolder: boolean;
  currentPage: number;
  itemsPerPage: number;
  onMoveItem: (draggedItem: Item, targetFolder: FolderItem) => Promise<void>;
  movingItemIds: Set<string>;
  selectedItemIds: Set<string>;
  onItemSelection: (itemId: string, isCtrlOrCmd: boolean, isShiftKey?: boolean, targetChecked?: boolean) => void;
  onClearSelection: () => void;
  onToggleSelectAll: () => void;

  // Pagination props
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;

  // Search mode
  isSearchMode?: boolean;
  query?: string;
  onClearSearch?: () => void;
  error?: unknown;
  onRetry?: () => unknown;
}

export const DriveContent: React.FC<Props> = ({
  currentFolder,
  parentFolder,
  folderId,
  selectedItemsCount,
  selectedFileItems,
  onDeleteClick,
  isDeleteLoading,
  currentSort,
  onSortChange,
  items,
  isRootFolder,
  currentPage,
  itemsPerPage,
  onMoveItem,
  movingItemIds,
  selectedItemIds,
  onItemSelection,
  onClearSelection,
  onToggleSelectAll,
  totalPages,
  onPageChange,
  isLoading,
  isSearchMode = false,
  query = "",
  onClearSearch = () => undefined,
  error,
  onRetry,
}) => {
  const authenticatedUser = useAuthenticatedUser();
  const [searchParams] = useSearchParams();
  // Reached from the Supplier or Renewal Centre, the folder is a Contract Family's document list, not a
  // My Folders location: search is left to My Folders. Read from the URL so the My Folders chrome never
  // shows while the folder and its relation load.
  const isContractFamilyView = isContractFamilyLink(searchParams);
  const { hasContractFamilies } = useContractFamiliesAccess();
  const visibleItems = React.useMemo(() => {
    const orderedItems = [
      ...items.filter(item => item.type === "folder"),
      ...items.filter(item => item.type === "file"),
    ];
    const startIndex = (currentPage - 1) * itemsPerPage;
    return orderedItems.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, items, itemsPerPage]);
  const directoryUserIds = collectDirectoryUserIds(visibleItems, authenticatedUser.id);
  const directoryQuery = useGetDirectoryUsersQuery(directoryUserIds, {
    skip: directoryUserIds.length === 0,
  });
  const usersById = React.useMemo(
    () => new Map((directoryQuery.data ?? []).map(user => [user.id, user])),
    [directoryQuery.data],
  );
  const canGroupFamilies = currentFolder
    ? !currentFolder.permissions.some(row => row.user_id === authenticatedUser.id && row.role === "viewer")
    : false;
  const canCreate = currentFolder ? canGroupFamilies : false;
  // The folder's supplier relation, from the URL when the Supplier Centre linked here, else looked up.
  const contractFolder = useSupplierContractFolder(
    currentFolder?.id,
    Boolean(currentFolder) && !isRootFolder && hasContractFamilies && !error,
  );
  // At the family folder the parent row leads out of the family; inside its subfolders it leads back up.
  // Hidden until the family folder is known, so it never flashes at the family folder.
  const familyFolderFseId = contractFolder?.folder?.fs_entry_id ?? contractFolder?.folder?.dms_fse_id;
  const hideParentRow =
    isContractFamilyView && (familyFolderFseId === undefined || familyFolderFseId === currentFolder?.id);

  // As with the rest of its chrome (AAHB-932), the same folder browsed to from My Folders stays standard.
  const isFamilyFolder =
    isContractFamilyView && familyFolderFseId !== undefined && familyFolderFseId === currentFolder?.id;
  const { mode: chosenViewMode, setMode: setViewMode } = useDocumentMapViewMode(authenticatedUser.id);
  const viewMode = isFamilyFolder ? chosenViewMode : "list";
  const {
    documentMap,
    isError: isDocumentMapError,
    generateMap,
    isRequestingMap,
  } = useFamilyFolderDocumentMap(currentFolder?.id, isFamilyFolder, contractFolder?.status);
  const mapSummary = React.useMemo(() => {
    if (documentMap?.status !== "generated" || !documentMap.document_map?.nodes.length) return null;
    const graph = buildDocumentGraph(documentMap.document_map, {
      view: "all",
      showRedundant: false,
    });
    return viewMode === "map" ? graphStructureSummary(graph) : mapBannerSummary(graph);
  }, [documentMap, viewMode]);
  const mapPanelProps = {
    response: documentMap,
    isError: isDocumentMapError,
    canGenerate: canGroupFamilies,
    onGenerate: () => void generateMap(),
    isRequestingGenerate: isRequestingMap,
  };
  const rolesByFseId = documentMap?.roles_by_fse_id;
  const splitRoleFor = React.useCallback(
    (item: Item) => (item.type === "file" ? documentRoleLabel(rolesByFseId?.[item.id]) : null),
    [rolesByFseId],
  );

  return (
    <>
      {currentFolder && (
        <DriveHeader
          parentFolder={parentFolder ?? null}
          currentFolder={currentFolder}
          isRootFolder={isRootFolder}
          askAIButton={true}
          addFilesButton={true}
          contractFolder={contractFolder}
          canCreateContractSummary={canGroupFamilies}
        />
      )}
      {currentFolder && contractFolder ? (
        <ContractSummaryStatusBanner
          folderFseId={currentFolder.id}
          contractFolder={contractFolder}
          mapSummary={mapSummary}
        />
      ) : null}
      {!currentFolder && (
        <DriveWorkspaceHeader currentFolder={undefined} parentFolder={null} isRootFolder={false} actions={null} />
      )}
      {!isContractFamilyView && (
        <DriveToolbar folderId={folderId} currentSort={currentSort} onSortChange={onSortChange} />
      )}
      {isFamilyFolder && !error ? <DocumentMapViewControl value={viewMode} onChange={setViewMode} /> : null}
      {!error && viewMode !== "map" && (
        <DriveActionBar
          selectedItemsCount={selectedItemsCount}
          selectedFileFseIds={selectedFileItems.map(item => item.id)}
          selectedFolderFseIds={items
            .filter(item => item.type === "folder" && selectedItemIds.has(item.id))
            .map(item => item.id)}
          currentFolderFseId={currentFolder?.id}
          currentFolderName={currentFolder?.name}
          canUseAdminBulk={(authenticatedUser.pfRoles ?? []).some(role =>
            ["organization_admin", "superadmin"].includes(role),
          )}
          canGroupFamilies={canGroupFamilies}
          hasContractFamilies={hasContractFamilies}
          onDeleteClick={onDeleteClick}
          isDeleteLoading={isDeleteLoading}
          currentSort={currentSort}
          onSortChange={onSortChange}
          isSearchMode={true}
        />
      )}
      {error ? (
        <DriveFatalError onRetry={onRetry} />
      ) : isLoading ? (
        <DriveSkeleton hideHeader={true} hideSearch={true} />
      ) : viewMode === "map" ? (
        <React.Suspense fallback={<Skeleton className="h-96 w-full" />}>
          <DocumentMapPanel variant="full" {...mapPanelProps} />
        </React.Suspense>
      ) : (
        <div className={viewMode === "split" ? "flex flex-col gap-4 xl:grid xl:grid-cols-2 xl:items-start" : undefined}>
          {viewMode === "split" ? (
            // SV-04: on a narrow screen the map stacks above the table.
            <div className="min-w-0 xl:order-2">
              <React.Suspense fallback={<Skeleton className="h-96 w-full" />}>
                <DocumentMapPanel variant="split" {...mapPanelProps} onOpenFullScreen={() => setViewMode("map")} />
              </React.Suspense>
            </div>
          ) : null}
          <div className="min-w-0 xl:order-1">
            <DriveList
              splitRoleFor={viewMode === "split" ? splitRoleFor : undefined}
              items={visibleItems}
              emptyState={
                <DriveEmptyState
                  query={isSearchMode ? query : ""}
                  canCreate={canCreate}
                  onClearSearch={onClearSearch}
                />
              }
              usersById={usersById}
              authenticatedUserId={authenticatedUser.id}
              parentFolder={hideParentRow ? null : (parentFolder ?? null)}
              isRootFolder={isRootFolder}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentFolderId={currentFolder?.id || ""}
              onMoveItem={onMoveItem}
              movingItemIds={movingItemIds}
              selectedItemIds={selectedItemIds}
              onItemSelection={onItemSelection}
              onClearSelection={onClearSelection}
              onToggleSelectAll={onToggleSelectAll}
            />
          </div>
        </div>
      )}
      {!isLoading && items.length > 0 && viewMode !== "map" && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
      )}
    </>
  );
};
